import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CalendarClock, ChevronDown, ChevronRight, ListChecks, PlayCircle, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const REASON_LABEL = {
    due: "Cadence due",
    readout_this_quarter: "Readout this quarter",
    gate_expected_this_quarter: "Gate expected this quarter",
    window_opening_90d: "Window opens ≤ 90 d",
    under_regulatory_review: "Under regulatory review",
    overdue: "Overdue",
    frontier_running_long: "Running long",
    momentum: "Momentum",
    sites_pending: "Sites pending",
};

const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");

/** Daily targeted re-evaluation — schedule, last run, plan preview and "Run now". */
export function ReevaluationPanel({ onRunStarted }) {
    const [open, setOpen] = useState(true);
    const [schedule, setSchedule] = useState(null);
    const [plan, setPlan] = useState(null);
    const [loadingPlan, setLoadingPlan] = useState(false);
    const [running, setRunning] = useState(false);

    const loadSchedule = useCallback(async () => {
        try {
            const r = await api.get("/v1/ripeness/reevaluations/schedule");
            setSchedule(r.data);
        } catch (e) { /* silent — admin-only panel */ }
    }, []);

    useEffect(() => {
        loadSchedule();
        const id = setInterval(loadSchedule, 30000);
        return () => clearInterval(id);
    }, [loadSchedule]);

    const previewPlan = useCallback(async () => {
        setLoadingPlan(true);
        try {
            const r = await api.get("/v1/ripeness/reevaluations/plan", { params: { max_programs: schedule?.default_max_programs || 200 } });
            setPlan(r.data);
        } catch (e) {
            toast.error(`Plan preview failed: ${formatApiErrorDetail(e)}`);
        } finally {
            setLoadingPlan(false);
        }
    }, [schedule]);

    const runNow = useCallback(async () => {
        if (!window.confirm("Start a targeted re-evaluation run now? Only planned tracks are re-scored (CT.gov is re-fetched).")) return;
        setRunning(true);
        try {
            const r = await api.post("/v1/ripeness/reevaluations", { reason: "manual run from QA dashboard" });
            toast.success(`Re-evaluation ${r.data.job_id} started — ${r.data.plan?.selected?.tracks ?? 0} tracks / ${r.data.plan?.selected?.programs ?? 0} programs`);
            onRunStarted?.(r.data);
            loadSchedule();
        } catch (e) {
            const detail = e?.response?.data?.detail;
            if (e?.response?.status === 409) toast.error(`A run is already active (${detail?.running_job_id || "—"})`);
            else toast.error(`Run failed: ${formatApiErrorDetail(e)}`);
        } finally {
            setRunning(false);
        }
    }, [loadSchedule, onRunStarted]);

    const last = schedule?.last_job;
    const active = last && ["queued", "processing"].includes(last.status);
    return (
        <div className="rounded-lg border border-slate-700/60 bg-slate-900/40 p-4" data-testid="reeval-panel">
            <button
                onClick={() => setOpen(!open)}
                className="w-full flex items-center justify-between text-xs uppercase tracking-wider text-slate-400 mb-2"
                data-testid="reeval-panel-toggle"
            >
                <span className="inline-flex items-center gap-1.5"><CalendarClock className="w-3.5 h-3.5" /> Daily re-evaluation</span>
                {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
            </button>
            {open ? (
                <div className="text-xs space-y-2">
                    <div className="text-slate-500">
                        Daily <span className="text-slate-300">{String(schedule?.in_app_fallback?.hour_utc ?? 3).padStart(2, "0")}:00 UTC</span>
                        {" "}· cron <code className="text-slate-400">{schedule?.cron?.expression || "0 3 * * *"}</code>
                        {schedule?.in_app_fallback?.enabled ? <> · in-app fallback on</> : null}
                        {" · "}cap <span className="text-slate-300" data-testid="reeval-cap">{schedule?.default_max_programs ?? "—"}</span> programs
                    </div>
                    <div className="text-slate-500">
                        Next automated run <span className="text-slate-300" data-testid="reeval-next-run">{fmt(schedule?.next_automated_run_at)}</span>
                        {schedule?.automated_run_today ? <span className="ml-1 text-emerald-300">· ran today</span> : null}
                    </div>
                    {last ? (
                        <div className="border border-slate-800 rounded p-2" data-testid="reeval-last-job">
                            <div className="flex items-center gap-2">
                                <span className={`rounded-full border px-2 py-0.5 uppercase tracking-wider ${
                                    last.status === "done" ? "border-emerald-400/40 text-emerald-300"
                                        : last.status === "failed" ? "border-rose-400/40 text-rose-300"
                                            : active ? "border-sky-400/40 text-sky-300" : "border-slate-500/30 text-slate-400"}`}>
                                    {active ? <RefreshCw className="w-3 h-3 inline mr-1 animate-spin" /> : null}{last.status}
                                </span>
                                <span className="text-slate-400 font-mono truncate">{last.job_id}</span>
                                <span className="ml-auto text-slate-500">{last.trigger}</span>
                            </div>
                            <div className="text-slate-500 mt-1">
                                {last.plan?.selected?.tracks ?? 0} tracks / {last.plan?.selected?.programs ?? 0} programs planned
                                {" · "}{last.progress?.programs_done || 0} done
                                {last.results ? <> · <span className="text-emerald-300">{last.results.tracks_changed} changed</span></> : null}
                            </div>
                            <div className="text-slate-600 mt-0.5">{fmt(last.finished_at || last.started_at || last.submitted_at)}</div>
                        </div>
                    ) : <div className="text-slate-500 italic">No run yet.</div>}
                    <div className="flex items-center gap-2 pt-1">
                        <button
                            onClick={previewPlan}
                            disabled={loadingPlan}
                            className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800/40 px-2 py-1 text-slate-200 hover:bg-slate-800 disabled:opacity-50"
                            data-testid="reeval-preview-btn"
                        >
                            {loadingPlan ? <RefreshCw className="w-3 h-3 animate-spin" /> : <ListChecks className="w-3 h-3" />} Preview plan
                        </button>
                        <button
                            onClick={runNow}
                            disabled={running || active}
                            title={active ? "A run is already active" : "Plan + run now"}
                            className="inline-flex items-center gap-1 rounded bg-emerald-500/80 hover:bg-emerald-500 px-2 py-1 text-white disabled:opacity-50 disabled:cursor-not-allowed"
                            data-testid="reeval-run-btn"
                        >
                            <PlayCircle className="w-3 h-3" /> Run now
                        </button>
                    </div>
                    {plan ? (
                        <div className="border border-slate-800 rounded p-2" data-testid="reeval-plan">
                            <div className="text-slate-300">
                                <b data-testid="reeval-plan-selected">{plan.selected.tracks}</b> tracks in <b>{plan.selected.programs}</b> programs selected
                                <span className="text-slate-500"> · {plan.candidates.tracks} candidates · {plan.deferred_programs} deferred · {plan.new_records} new records</span>
                            </div>
                            <div className="flex flex-wrap gap-1 mt-1.5">
                                {Object.entries(plan.by_reason).sort((a, b) => b[1] - a[1]).map(([k, n]) => (
                                    <span key={k} className="rounded-full border border-slate-700 px-2 py-0.5 text-[10px] text-slate-300" data-testid={`reeval-reason-${k}`}>
                                        {REASON_LABEL[k] || k} <span className="font-mono text-slate-500">{n}</span>
                                    </span>
                                ))}
                            </div>
                            {plan.programs.slice(0, 5).map((p) => (
                                <div key={p.program_id} className="text-slate-500 mt-1 truncate">
                                    <span className="text-slate-300">{p.compound || p.program_id}</span> · {p.tracks.length} track{p.tracks.length > 1 ? "s" : ""} · {p.tracks[0]?.reasons.map((r) => REASON_LABEL[r] || r).join(", ")}
                                </div>
                            ))}
                        </div>
                    ) : null}
                </div>
            ) : null}
        </div>
    );
}
